import { useCallback, useEffect, useId, useRef, useState } from "react"
import Link from "next/link"

import {
  Badge,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type BadgeProps,
} from "@coval/ui"

import type { SubscriptionResponse } from "../types"
import { usageBandCopy, type UsageBandState } from "../utils/usageBand"

interface UsageRemainingChipProps {
  state: UsageBandState
  subscription: SubscriptionResponse
  /** Where the chip sends someone who wants the detail: Settings → Usage. */
  href: string
}

const TONE: Record<UsageBandState["band"], BadgeProps["variant"]> = {
  approaching: "warning",
  close: "warning",
  nearly: "warning",
  over: "danger",
}

const BAR_TONE: Record<UsageBandState["band"], string> = {
  approaching: "bg-coval-warning-border",
  close: "bg-coval-warning-border",
  nearly: "bg-coval-warning-border",
  over: "bg-coval-danger-border",
}

// What the chip is short for. The panel carries the numbers, so the chip says
// how close the organization is rather than counting down at it.
const SHORT_UNIT: Record<UsageBandState["dimension"], string> = {
  simulation_minutes: "minutes",
  conversation_uploads: "uploads",
}

const LONG_UNIT: Record<UsageBandState["dimension"], string> = {
  simulation_minutes: "simulation minutes",
  conversation_uploads: "conversation uploads",
}

// The headline above already names the dimension, so the count below it says
// only what is being counted.
const COUNT_UNIT: Record<UsageBandState["dimension"], string> = {
  simulation_minutes: "minutes",
  conversation_uploads: "uploads",
}

function formatAmount(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

function countsSentence(state: UsageBandState): string {
  return `${formatAmount(state.used)} of ${formatAmount(state.limit)} ${COUNT_UNIT[state.dimension]}`
}

function remainingLabel(state: UsageBandState): string {
  const unit = SHORT_UNIT[state.dimension]
  if (state.band === "over") {
    // Not "out of" — nothing is enforced here, so the chip states the position
    // rather than a stop.
    return `Over plan ${unit}`
  }
  return state.band === "nearly" ? `Almost out of ${unit}` : `Low on ${unit}`
}

/**
 * What is left of the allowance, at the point of spend. It appears from the
 * first rung rather than always: below 80% there is nothing to say, and a
 * permanent counter beside the Launch button would be read once and then never
 * again.
 *
 * The panel opens on hover and holds a working link, so it is a popover driven
 * by pointer rather than a tooltip: tooltip content is not meant to be reached
 * with the pointer, and a link inside one is decoration. The chip itself goes to
 * the same place, so a click never has to find the link.
 */
export const UsageRemainingChip = ({
  state,
  subscription,
  href,
}: UsageRemainingChipProps) => {
  const { title } = usageBandCopy(state, subscription)
  const percent = Math.min(Math.round(state.ratio * 100), 100)
  // The panel is portaled and opens on hover, so it is not in the tab order.
  // The counts it carries are attached to the chip itself instead, which is
  // where a keyboard or screen reader lands and which already leads to the
  // same page the panel's link does.
  const countsId = useId()
  const [open, setOpen] = useState(false)
  // The pointer has to cross a gap to reach the panel, so closing waits long
  // enough for that trip and is cancelled the moment the panel is entered.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const openNow = useCallback(() => {
    cancelClose()
    setOpen(true)
  }, [cancelClose])

  const closeSoon = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), 150)
  }, [cancelClose])

  useEffect(() => cancelClose, [cancelClose])
  // The period qualifies the headline here rather than the count below it, so
  // the numbers read as a pair. Derived from the shared copy so the deck stays
  // the one place the sentence is written.
  const headline = `${title.replace(/\.$/, "")} this period.`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Link
          href={href}
          aria-label={title}
          // Inline by default, so the anchor's box would be text-height and the
          // hit area and focus ring would sit inside the chip rather than on it.
          className="inline-flex"
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          onFocus={openNow}
          onBlur={closeSoon}
          aria-describedby={countsId}
        >
          <Badge
            variant={TONE[state.band]}
            // `chip` is the control-height size, so it sits level with the
            // Launch button and the controls beside it rather than above them.
            size="chip"
            className="tabular-nums"
          >
            {remainingLabel(state)}
          </Badge>
        </Link>
      </PopoverTrigger>
      <span id={countsId} className="sr-only">
        {countsSentence(state)} used this period.
      </span>
      <PopoverContent
        align="end"
        // Fixed width: the panel's own text would otherwise set it, and a single
        // sentence stretches an overlay across the window.
        className="flex w-64 flex-col gap-2 p-6 text-sm"
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        // Focus reaching the panel holds it open, so anything inside it stays
        // usable once reached rather than closing under the move off the chip.
        onFocusCapture={openNow}
        onBlur={closeSoon}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <p className="font-medium text-coval-text">{headline}</p>
        <div
          role="progressbar"
          aria-label={LONG_UNIT[state.dimension]}
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-coval-border"
        >
          <div
            className={`h-full rounded-full ${BAR_TONE[state.band]}`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="text-coval-text tabular-nums">{countsSentence(state)}</p>
        <Link
          href={href}
          className="text-coval-text-muted underline underline-offset-2 hover:text-coval-text"
        >
          View usage
        </Link>
      </PopoverContent>
    </Popover>
  )
}
